import type React from 'react';

export type ModuleType =
  | 'listing-summary'
  | 'tour-confirmation'
  | 'amenities'
  | 'neighborhood'
  | 'nearby-schools'
  | 'about-building'
  | 'housemate-details'
  | 'pets'
  | 'competition'
  | 'units'
  | 'property-manager'
  | 'cost-and-fees'
  | 'pricing-comparison'
  | 'inline-contact'
  | 'listing-feedback'
  | 'similar-listings'
  | 'zrm-upsell'
  | 'recently-viewed'
  | 'faq';

// The static `SurroundingAreaStats` type in listingDetails.type.ts says `string | null`, but at
// runtime `ListingAreas` hydrates these into `AreaStat` objects. The legacy SurroundingStats
// module uses `@ts-nocheck` to paper over this. For the moduleConfig gate we only need truthiness,
// so accept anything non-undefined.
type AreaStatSnapshot = unknown;

type ListingSnapshot =
  | {
      active?: boolean;
      isAllowedToRetain?: boolean;
      trusted?: boolean;
      isApartmentBldg?: boolean;
      showPricingStats?: boolean;
      amenities?: {
        petPolicies?: {
          dog?: unknown;
          cat?: unknown;
        } | null;
      };
      areas?: {
        surroundingAreaStats?: {
          neighborhood?: AreaStatSnapshot;
          city?: AreaStatSnapshot;
          county?: AreaStatSnapshot;
          zip?: AreaStatSnapshot;
        };
      };
      details?: {
        roomForRent?: {
          roommateDetails?: {
            description?: string;
            roommatesInfo?: string;
            petsInfo?: string;
          };
          preferredNewRoommate?: string;
        };
      };
      popularity?: {
        summary?: {
          viewed?: number;
        };
      };
    }
  | null
  | undefined;

const canRetainData = (listing: ListingSnapshot): boolean =>
  listing?.active !== false || listing?.isAllowedToRetain !== false;

export type ModuleConfig = {
  id: string;
  type: ModuleType;
  component: React.ComponentType;
  enabled?: boolean;
  title?: string;
  shouldRender?: (listing: ListingSnapshot) => boolean;
  /**
   * When true, the module is hidden until the client has hydrated. Used for modules whose data
   * (e.g. `popularity` summary counters) is only reliable after client load; rendering on the
   * server would otherwise show stale/empty state that flashes on hydrate.
   */
  requiresClientLoaded?: boolean;
};

// Module type constants for easy reordering
export const LISTING_SUMMARY = 'listing-summary' as const;
export const TOUR_CONFIRMATION = 'tour-confirmation' as const;
export const AMENITIES = 'amenities' as const;
export const NEIGHBORHOOD = 'neighborhood' as const;
export const NEARBY_SCHOOLS = 'nearby-schools' as const;
export const ABOUT_BUILDING = 'about-building' as const;
export const HOUSEMATE_DETAILS = 'housemate-details' as const;
export const PETS = 'pets' as const;
export const COMPETITION = 'competition' as const;
export const UNITS = 'units' as const;
export const PROPERTY_MANAGER = 'property-manager' as const;
export const COST_AND_FEES = 'cost-and-fees' as const;
export const PRICING_COMPARISON = 'pricing-comparison' as const;
export const INLINE_CONTACT = 'inline-contact' as const;
export const LISTING_FEEDBACK = 'listing-feedback' as const;
export const SIMILAR_LISTINGS = 'similar-listings' as const;
export const ZRM_UPSELL = 'zrm-upsell' as const;
export const RECENTLY_VIEWED = 'recently-viewed' as const;
export const FAQ = 'faq' as const;

import { TourConfirmationBanner } from './TourConfirmationModule';
import { NeighborhoodModule } from './NeighborhoodModule/NeighborhoodModule';
import { NearbySchoolsModule } from './NearbySchoolsModule';
import { AboutTheBuildingModule } from './AboutTheBuildingModule';
import { HousemateDetailsModule } from './HousemateDetailsModule';
import { ListingSummaryModule } from './ListingSummaryModule';
import { PetsModule } from './PetsModule';
import { CompetitionModule } from './CompetitionModule';
import { UnitsModule } from './UnitsModule';
import { PropertyManagerModule } from './PropertyManagerModule';
import { CostAndFeesModule } from './CostAndFeesModule';
import { PricingComparisonModule } from './PricingComparisonModule';
import { AmenitiesModule } from './AmenitiesModule';
import { ListingFeedbackModule } from './ListingFeedbackModule';
import { SimilarListingsModule } from './SimilarListingsModule';
import { RecentlyViewedModule } from './RecentlyViewedModule';
import { FaqModule } from './FaqModule';
import { InlineContactModule } from './InlineContactModule';
import ZrmUpsellCarousel from 'app/shared/modules/ZrmUpsellCarousel';

const isInactiveListing = (listing: ListingSnapshot): boolean => listing?.active === false;

// Module definitions lookup
const moduleDefinitions: Record<ModuleType, Omit<ModuleConfig, 'type'>> = {
  [LISTING_SUMMARY]: {
    id: 'listing-summary-module',
    component: ListingSummaryModule,
    enabled: true,
  },
  [TOUR_CONFIRMATION]: {
    id: 'tour-confirmation-module',
    component: TourConfirmationBanner,
    enabled: true,
    title: 'Your booked tour',
  },
  [AMENITIES]: {
    id: 'amenities-module',
    component: AmenitiesModule,
    enabled: true,
    title: 'What you get',
    shouldRender: canRetainData,
  },
  [NEIGHBORHOOD]: {
    id: 'neighborhood-module',
    component: NeighborhoodModule,
    enabled: true,
    title: 'The neighborhood',
  },
  [NEARBY_SCHOOLS]: {
    id: 'nearby-schools-module',
    component: NearbySchoolsModule,
    enabled: true,
    title: 'Nearby schools',
    shouldRender: canRetainData,
  },
  [ABOUT_BUILDING]: {
    id: 'about-building-module',
    component: AboutTheBuildingModule,
    enabled: true,
    title: 'About the building',
    shouldRender: canRetainData,
  },
  [HOUSEMATE_DETAILS]: {
    id: 'housemate-details-module',
    component: HousemateDetailsModule,
    enabled: true,
    title: 'Housemate details',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      const roomForRent = listing?.details?.roomForRent;
      const roommateDetails = roomForRent?.roommateDetails;
      return Boolean(
        roommateDetails?.description ||
        roommateDetails?.roommatesInfo ||
        roommateDetails?.petsInfo ||
        roomForRent?.preferredNewRoommate,
      );
    },
  },
  [PETS]: {
    id: 'pets-module',
    component: PetsModule,
    enabled: true,
    title: 'Pets',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      const petPolicies = listing?.amenities?.petPolicies;
      return petPolicies != null && (petPolicies.dog != null || petPolicies.cat != null);
    },
  },
  [COMPETITION]: {
    id: 'competition-module',
    component: CompetitionModule,
    enabled: true,
    title: 'Competition for this rental',
    requiresClientLoaded: true,
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      return (listing?.popularity?.summary?.viewed ?? 0) > 0;
    },
  },
  [UNITS]: {
    id: 'units-module',
    component: UnitsModule,
    enabled: true,
    title: 'Units available in this building',
    shouldRender: (listing) => canRetainData(listing) && listing != null && listing.trusted === true,
  },
  [PROPERTY_MANAGER]: {
    id: 'property-manager-module',
    component: PropertyManagerModule,
    enabled: true,
    title: 'The property manager',
    shouldRender: canRetainData,
  },
  [COST_AND_FEES]: {
    id: 'cost-and-fees-module',
    component: CostAndFeesModule,
    enabled: true,
    title: 'Costs, fees, and lease terms',
    shouldRender: canRetainData,
  },
  [PRICING_COMPARISON]: {
    id: 'pricing-comparison-module',
    component: PricingComparisonModule,
    enabled: true,
    title: 'Pricing comparison',
    shouldRender: (listing) => {
      if (!canRetainData(listing)) return false;
      if (listing?.showPricingStats !== true) return false;
      if (listing?.isApartmentBldg) return false;
      const stats = listing?.areas?.surroundingAreaStats;
      return Boolean(stats?.neighborhood || stats?.city || stats?.county);
    },
  },
  [INLINE_CONTACT]: {
    id: 'inline-contact-module',
    component: InlineContactModule,
    enabled: true,
    shouldRender: canRetainData,
  },
  [LISTING_FEEDBACK]: {
    id: 'listing-feedback-module',
    component: ListingFeedbackModule,
    enabled: true,
  },
  [SIMILAR_LISTINGS]: {
    id: 'similar-listings-module',
    component: SimilarListingsModule,
    enabled: true,
    title: 'Find similar homes',
    shouldRender: isInactiveListing,
  },
  [ZRM_UPSELL]: {
    id: 'zrm-upsell-module',
    component: ZrmUpsellCarousel,
    enabled: true,
    shouldRender: isInactiveListing,
  },
  [RECENTLY_VIEWED]: {
    id: 'recently-viewed-module',
    component: RecentlyViewedModule,
    enabled: true,
    title: 'Recently viewed homes',
    requiresClientLoaded: true,
  },
  [FAQ]: {
    id: 'faq-module',
    component: FaqModule,
    enabled: true,
    title: 'Frequently asked questions',
    shouldRender: (listing) => listing?.trusted === true && listing?.active !== false,
  },
};

// Simply change the order of constants in this array to reorder modules
const moduleOrder: Array<ModuleType> = [
  LISTING_SUMMARY,
  TOUR_CONFIRMATION,
  SIMILAR_LISTINGS,
  ZRM_UPSELL,
  AMENITIES,
  UNITS,
  COST_AND_FEES,
  PRICING_COMPARISON,
  INLINE_CONTACT,
  PETS,
  COMPETITION,
  NEIGHBORHOOD,
  NEARBY_SCHOOLS,
  ABOUT_BUILDING,
  HOUSEMATE_DETAILS,
  PROPERTY_MANAGER,
  RECENTLY_VIEWED,
  FAQ,
  LISTING_FEEDBACK,
];

// Generated configuration based on module order
export const moduleConfig: Array<ModuleConfig> = moduleOrder.map((type) => ({
  type,
  ...moduleDefinitions[type],
}));
